import { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useEdit } from '../lib/editContext';
import { getImageUrl } from '../lib/utils-img';
import API from '../lib/api';
import { toast } from 'sonner';

export default function EditableImage({ page, field, src, alt = '', className = '', style = {}, ...rest }) {
  const { editMode, updateField, changes } = useEdit();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pendingSrc = changes?.[page]?.[field];
  const displaySrc = getImageUrl(pendingSrc || src);

  if (!editMode) {
    return <img src={displaySrc} alt={alt} className={className} style={style} {...rest} />;
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateField(page, field, data.url);
      toast.success('Image updated — click Save to keep');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="relative group/edit-img" data-editable-page={page} data-editable-field={field}>
      <img src={displaySrc} alt={alt} className={className} style={style} {...rest} />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="editable-image-btn absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/edit-img:opacity-100 transition-opacity cursor-pointer z-10"
        type="button"
      >
        {uploading
          ? <Loader2 className="w-8 h-8 text-white animate-spin" />
          : <div className="flex flex-col items-center gap-2 text-white">
              <Camera className="w-8 h-8" />
              <span className="text-xs font-medium uppercase tracking-widest">Replace Image</span>
            </div>
        }
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
}
